export const domainRouting = {
	"app.fergmar.com": {
		entry: ["localhost", "app.fergmar.com"],
		destination: null,
		faviconSlug: "fergmar-information-terminal"
	},
	"go.fergmar.com": {
		entry: ["go.fergmar.localhost", "go.fergmar.com"],
		destination: "/go-fergmar-com",
		faviconSlug: "fergmar"
	},
	"fergmar.com": {
		entry: ["fergmar.localhost", "fergmar.com", "www.fergmar.com"],
		destination: "/fergmar-com",
		faviconSlug: "fergmar"
	},
	"fergmarcapital.com": {
		entry: ["fergmarcapital.localhost", "fergmarcapital.com", "www.fergmarcapital.com"],
		destination: "/fergmarcapital-com",
		hasBlog: true,
		faviconSlug: "fergmar-capital"
	},
	"fergmarproperties.com": {
		entry: [
			"fergmarproperties.localhost",
			"fergmarproperties.com",
			"www.fergmarproperties.com"
		],
		destination: "/fergmarproperties-com",
		faviconSlug: "fergmar"
	},
	"brand.fergmar.com": {
		entry: ["brand.localhost", "brand.fergmar.com", "www.brand.fergmar.com"],
		destination: "/brand",
		faviconSlug: "fergmar"
	},
	"thevillastx.com": {
		entry: ["thevillastx.localhost", "thevillastx.com", "www.thevillastx.com"],
		destination: "/rental-template/thevillastx-com",
		faviconSlug: "the-villas"
	},
	"liveatparkwood.com": {
		entry: ["liveatparkwood.localhost", "liveatparkwood.com", "www.liveatparkwood.com"],
		destination: "/rental-template/liveatparkwood-com",
		faviconSlug: "parkwood"
	},
	"fergmarhomebuyers.com": {
		entry: [
			"fergmarhomebuyers.localhost",
			"fergmarhomebuyers.com",
			"www.fergmarhomebuyers.com"
		],
		destination: "/fergmarhomebuyers-com",
		hasBlog: true,
		faviconSlug: "fergmar-home-buyers"
	}
} as const;

const legacyImageRedirectPaths: Readonly<Record<string, string>> = {
	"/fergmar-capital-email-logo.png":
		"/image/upload/f_auto,q_auto,w_300,e_trim/brand/fergmar-capital/fergmar-capital-long-light",
	"/logowhitebg.webp": "/image/upload/f_auto,q_auto,w_300,e_trim/brand/fergmar/fergmar-logo-light"
};

const sharedLegacyRouteRedirects: Readonly<Record<string, string>> = {
	"/forms/getcash": "/getpaid",
	"/sitemap": "/sitemap.xml"
};

const fitLegacyRouteRedirects: Readonly<Record<string, string>> = {
	"/rental-availability": "/rentalAvailability"
};

const CLOUDINARY_ORIGIN = "https://res.cloudinary.com";
const INVESTMENT_MANAGEMENT_PREFIX = "/services/investment-management";
const INVESTMENT_MANAGEMENT_ORIGIN = "https://fergmarcapital.com";

export type LegacyRedirectHost = "fit" | "www";

export type LegacyRedirectTarget = {
	kind: "image" | "route" | "prefix";
	location: string;
	externalOrigin?: string;
};

export function getLegacyRedirectTarget(
	pathname: string,
	cloudinaryCloudName: string,
	host: LegacyRedirectHost
): LegacyRedirectTarget | null {
	const imagePath = legacyImageRedirectPaths[pathname];
	if (imagePath !== undefined) {
		return {
			kind: "image",
			location: `${CLOUDINARY_ORIGIN}/${cloudinaryCloudName}${imagePath}`,
			externalOrigin: CLOUDINARY_ORIGIN
		};
	}

	const routeRedirect =
		sharedLegacyRouteRedirects[pathname] ??
		(host === "fit" ? fitLegacyRouteRedirects[pathname] : undefined);
	if (routeRedirect !== undefined) return { kind: "route", location: routeRedirect };

	if (pathname.startsWith(INVESTMENT_MANAGEMENT_PREFIX)) {
		const suffix = pathname.slice(INVESTMENT_MANAGEMENT_PREFIX.length);
		return {
			kind: "prefix",
			location: `${INVESTMENT_MANAGEMENT_ORIGIN}${suffix || "/"}`,
			externalOrigin: INVESTMENT_MANAGEMENT_ORIGIN
		};
	}

	return null;
}

export const blogSiteSlugs = Object.values(domainRouting).flatMap((site) =>
	"hasBlog" in site && site.destination ? [site.destination.slice(1)] : []
);

export type DisplaySite = keyof typeof domainRouting;

export const displaySiteKeys = Object.keys(domainRouting) as DisplaySite[];
export function getDomainKey(hostname: string): DisplaySite | null {
	for (const key of displaySiteKeys) {
		const entry: readonly string[] = domainRouting[key].entry;
		if (entry.includes(hostname)) return key;
	}
	return null;
}

const allEntryHostnames = [
	...new Set(Object.values(domainRouting).flatMap((entry) => entry.entry))
];

const isLocalhostHost = (hostname: string) => hostname.endsWith("localhost");

const withWwwVariants = (hosts: string[]) => [
	...new Set(hosts.flatMap((host) => (host.startsWith("www.") ? [host] : [host, `www.${host}`])))
];

export const corsProdOrigins = withWwwVariants(
	allEntryHostnames.filter((hostname) => !isLocalhostHost(hostname))
).map((hostname) => `https://${hostname}`);

const devLocalhostHosts = new Set<string>(allEntryHostnames.filter(isLocalhostHost));

export const isDevOrigin = (origin: string): boolean => {
	try {
		const { protocol, hostname } = new URL(origin);
		return protocol === "http:" && devLocalhostHosts.has(hostname);
	} catch {
		return false;
	}
};
